/**
 * FormRouter - Sistema unificato per tutti i form dell'applicazione
 *
 * Un solo punto di ingresso per aprire qualsiasi form.
 * Tutti i form si aprono al 50% della larghezza usando DetailPanel.
 *
 * Usage:
 * <FormRouter
 *   type="task"
 *   entityId="task-123"
 *   projectId="proj-456"
 *   onClose={() => {}}
 *   onSave={(data) => {}}
 * />
 */

import { lazy, Suspense } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type FormType =
  | 'task'
  | 'project'
  | 'event'
  | 'contact'
  | 'call'
  | 'videocall'
  | 'email'
  | 'message'
  | 'invoice'
  | 'milestone'
  | 'time-entry'
  | 'timer-stop';

export interface FormRouterProps {
  /** Tipo di form da aprire */
  type: FormType;
  /** ID dell'entità (per edit mode) */
  entityId?: string;
  /** ID del progetto (per form collegati a progetti) */
  projectId?: string;
  /** ID del contatto (per form collegati a contatti) */
  contactId?: string;
  /** ID della conversazione (per messaggi) */
  conversationId?: string;
  /** Canale di comunicazione (per messaggi) */
  channel?: 'whatsapp' | 'telegram' | 'sms' | 'email';
  /** Callback alla chiusura */
  onClose: () => void;
  /** Callback al salvataggio */
  onSave?: (data: any) => void;
  /** Dati iniziali per pre-popolare il form */
  initialData?: Record<string, any>;
}

// =============================================================================
// LAZY IMPORTS (code splitting)
// =============================================================================

const TaskWidget = lazy(() =>
  import('../projects/TaskWidget').then(m => ({ default: m.TaskWidget }))
);

const ProjectWidget = lazy(() =>
  import('../projects/ProjectWidget').then(m => ({ default: m.ProjectWidget }))
);

const EventWidget = lazy(() =>
  import('../calendar/EventWidget').then(m => ({ default: m.EventWidget }))
);

const ContactWidget = lazy(() =>
  import('../hub/ContactWidget').then(m => ({ default: m.ContactWidget }))
);

const CallWidget = lazy(() =>
  import('../hub/CallWidget').then(m => ({ default: m.CallWidget }))
);

const EmailWidget = lazy(() =>
  import('../hub/EmailWidget').then(m => ({ default: m.EmailWidget }))
);

const MessageWidget = lazy(() =>
  import('../hub/MessageWidget').then(m => ({ default: m.MessageWidget }))
);

const InvoiceWidget = lazy(() =>
  import('../accounting/InvoiceWidget').then(m => ({ default: m.InvoiceWidget }))
);

const MilestoneWidget = lazy(() =>
  import('../projects/MilestoneWidget').then(m => ({ default: m.MilestoneWidget }))
);

const TimeEntryWidget = lazy(() =>
  import('../projects/TimeEntryWidget').then(m => ({ default: m.TimeEntryWidget }))
);

const TimerStopForm = lazy(() =>
  import('../timer/TimerStopForm').then(m => ({ default: m.TimerStopForm }))
);

const VideoCallWidget = lazy(() =>
  import('../videocall/VideoCallWidget').then(m => ({ default: m.VideoCallWidget }))
);

// =============================================================================
// LOADING FALLBACK
// =============================================================================

function FormLoading() {
  return (
    <div className="form-router__loading">
      <div className="form-router__spinner" />
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FormRouter({
  type,
  entityId,
  projectId,
  contactId,
  conversationId,
  channel,
  onClose,
  onSave,
  initialData,
}: FormRouterProps) {
  return (
    <Suspense fallback={<FormLoading />}>
      {type === 'task' && (
        <TaskWidget
          projectId={projectId || ''}
          taskId={entityId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'project' && (
        <ProjectWidget
          projectId={entityId}
          onClose={onClose}
          onSave={onSave}
        />
      )}

      {type === 'event' && (
        <EventWidget
          isOpen={true}
          eventId={entityId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'contact' && (
        <ContactWidget
          isOpen={true}
          contactId={entityId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'call' && (
        <CallWidget
          isOpen={true}
          callId={entityId}
          contactId={contactId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'videocall' && (
        <VideoCallWidget
          isOpen={true}
          onClose={onClose}
          onSave={onSave}
        />
      )}

      {type === 'email' && (
        <EmailWidget
          isOpen={true}
          emailId={entityId}
          contactId={contactId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'message' && conversationId && channel && (
        <MessageWidget
          conversationId={conversationId}
          channel={channel}
          onClose={onClose}
          onSend={onSave || (() => onClose())}
        />
      )}

      {type === 'invoice' && (
        <InvoiceWidget
          invoiceId={entityId}
          projectId={projectId}
          contactId={contactId}
          onClose={onClose}
          onSave={onSave}
        />
      )}

      {type === 'milestone' && (
        <MilestoneWidget
          projectId={projectId || ''}
          milestoneId={entityId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'time-entry' && (
        <TimeEntryWidget
          projectId={projectId || ''}
          entryId={entityId}
          onClose={onClose}
          onSave={onSave || (() => onClose())}
        />
      )}

      {type === 'timer-stop' && (
        <TimerStopForm
          onClose={onClose}
          onSave={onSave}
        />
      )}
    </Suspense>
  );
}

// =============================================================================
// HOOK: useFormRouter
// =============================================================================

import { useState, useCallback } from 'react';

interface FormState {
  isOpen: boolean;
  type: FormType | null;
  entityId?: string;
  projectId?: string;
  contactId?: string;
  conversationId?: string;
  channel?: 'whatsapp' | 'telegram' | 'sms' | 'email';
  initialData?: Record<string, any>;
}

export function useFormRouter() {
  const [state, setState] = useState<FormState>({
    isOpen: false,
    type: null,
  });

  const openForm = useCallback((
    type: FormType,
    options?: {
      entityId?: string;
      projectId?: string;
      contactId?: string;
      conversationId?: string;
      channel?: 'whatsapp' | 'telegram' | 'sms' | 'email';
      initialData?: Record<string, any>;
    }
  ) => {
    setState({
      isOpen: true,
      type,
      entityId: options?.entityId,
      projectId: options?.projectId,
      contactId: options?.contactId,
      conversationId: options?.conversationId,
      channel: options?.channel,
      initialData: options?.initialData,
    });
  }, []);

  const closeForm = useCallback(() => {
    setState({
      isOpen: false,
      type: null,
    });
  }, []);

  return {
    isOpen: state.isOpen,
    type: state.type,
    entityId: state.entityId,
    projectId: state.projectId,
    contactId: state.contactId,
    conversationId: state.conversationId,
    channel: state.channel,
    initialData: state.initialData,
    openForm,
    closeForm,
  };
}
